import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SiteData } from '../../types';

interface CountryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  countryName: string;
  sites: SiteData[];
  onSiteClick?: (site: SiteData) => void;
}

export const CountryDetailsModal = ({ isOpen, onClose, countryName, sites, onSiteClick }: CountryDetailsModalProps) => {
  // Group sites by customer
  const sitesByCustomer = sites.reduce((acc, site) => {
    if (!acc[site.customer]) {
      acc[site.customer] = [];
    }
    acc[site.customer].push(site);
    return acc;
  }, {} as Record<string, SiteData[]>);

  const customers = Object.keys(sitesByCustomer);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground">
            {countryName} - Customers & Sites
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Customers</h3>
              <p className="text-2xl font-bold text-foreground">{customers.length}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Sites</h3>
              <p className="text-2xl font-bold text-foreground">{sites.length}</p>
            </div>
          </div>

          {customers.map((customer) => (
            <div key={customer} className="bg-card border border-border rounded-lg p-4">
              <h3 
                className="text-lg font-semibold text-foreground mb-3 hover:text-primary cursor-pointer transition-colors"
                onClick={() => {
                  // Find any site from this customer to trigger the modal
                  const customerSite = sitesByCustomer[customer][0];
                  if (customerSite) onSiteClick?.(customerSite);
                }}
              >
                {customer}
              </h3>
              <div className="space-y-2">
                {sitesByCustomer[customer]
                  .sort((a, b) => (b.numSessionsOnServerLast12Month || 0) - (a.numSessionsOnServerLast12Month || 0))
                  .map((site) => (
                  <div key={site.siteName} className="flex items-center justify-between p-3 bg-muted/50 rounded-md hover:bg-muted/70 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${site.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <div>
                        <p 
                          className="font-medium text-foreground hover:text-primary cursor-pointer transition-colors"
                          onClick={() => onSiteClick?.(site)}
                        >
                          {site.siteName}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <span 
                            className="hover:text-primary cursor-pointer transition-colors"
                            onClick={() => onSiteClick?.(site)}
                          >
                            {site.siteType}
                          </span> • Version {site.mlVersion}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">
                        {site.numSessionsOnServerLast12Month || 0} sessions
                      </p>
                      <p className="text-xs text-muted-foreground">Last 12 months</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};